import React from 'react';
import { observer } from 'src/utils/mobx-react';
import { ClickOutsideHandler } from 'src/domains/common/components/clickOutside';
import { cn } from 'src/domains/common/components/utils';

const DialogOverlay = observer('DialogOverlay', ({ children, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
    <div
        className='fixed inset-0 z-50 bg-accent/80'
        {...props}
    >
        {children}
    </div>
));

const DialogContent = observer('DialogContent', ({ children, className }: React.HTMLAttributes<HTMLDivElement>) => (
    <div
        className={cn(
            'w-auto fixed left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50 p-6 bg-white shadow-lg rounded-lg',
            className
        )}
    >
        {children}
    </div>
));

export const DialogHeader = observer('DialogHeader', ({ children }: React.HTMLAttributes<HTMLDivElement>) => (
    <div className='flex flex-col space-y-1.5 text-left'>{children}</div>
));

export const DialogFooter = observer('DialogFooter', ({ children, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
    <div
        className='flex justify-end gap-x-2 mt-4'
        {...props}
    >
        {children}
    </div>
));

export const DialogTitle = observer('DialogTitle', ({ children, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
    <div
        className='text-lg font-semibold tracking-tight'
        {...props}
    >
        {children}
    </div>
));

export const DialogDescription = observer(
    'DialogDescription',
    ({ children, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
        <div
            className='text-sm text-muted-foreground'
            {...props}
        >
            {children}
        </div>
    )
);

interface DialogPropsType {
    children: React.ReactNode;
    hasOverlay?: boolean;
    className?: string;
    dataTest?: string;
    onClickOutside?: () => void;
}
export const Dialog = observer(
    'Dialog',
    ({ children, onClickOutside, hasOverlay = true, className, dataTest }: DialogPropsType): JSX.Element => {
        return (
            <div data-test={dataTest}>
                {hasOverlay && <DialogOverlay data-test='dialog-overlay' />}
                <ClickOutsideHandler onClickOutside={() => onClickOutside?.()}>
                    <DialogContent className={className}>{children}</DialogContent>
                </ClickOutsideHandler>
            </div>
        );
    }
);
